import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { type AnchoredPanelSide, placeAnchoredPanel } from "../lib/ui/anchored-panel";

/**
 * Garde un panneau flottant ancré (popover / picker) DANS le viewport visible —
 * patron DS §6.12, décision géométrique dans `lib/ui/anchored-panel.ts`.
 *
 * L'ancre est le bloc conteneur du panneau, c'est-à-dire son `offsetParent` :
 * le wrapper `position: relative` qui porte le déclencheur. Rien à câbler côté
 * appelant, et aucune fenêtre de désynchronisation entre deux refs.
 *
 * Ce que le hook écrit sur l'élément : DEUX custom properties de géométrie
 * MESURÉE (`--ds-popover-shift`, `--ds-popover-max-h`) consommées par la classe
 * `.ds-popover`. Aucun style de design en ligne : couleurs, rayons, ombre,
 * largeur et gouttière restent des jetons et des classes (`global.css`). Un
 * décalage horizontal ne peut pas vivre dans une classe : il est continu — sur
 * un écran de 390 px, une ancre au milieu de la phrase ne tient ni aligné à
 * gauche ni aligné à droite.
 *
 * Recalcule à l'ouverture, au redimensionnement et au défilement — y compris
 * ceux du viewport VISUEL, qui sont les seuls signaux d'apparition du clavier
 * iOS (aucun plugin Capacitor requis).
 */
export function useAnchoredPanel(open: boolean): {
  panelRef: (el: HTMLDivElement | null) => void;
  side: AnchoredPanelSide;
} {
  const panelElRef = useRef<HTMLDivElement | null>(null);
  const [side, setSide] = useState<AnchoredPanelSide>("below");

  const measure = useCallback(() => {
    const panel = panelElRef.current;
    const anchor = panel?.offsetParent;
    // jsdom (et un panneau démonté) n'ont pas de bloc conteneur : on ne mesure
    // rien plutôt que de poser une géométrie fausse.
    if (!panel || !(anchor instanceof HTMLElement)) return;

    const visual = window.visualViewport;
    const placement = placeAnchoredPanel({
      anchor: anchor.getBoundingClientRect(),
      panelWidth: panel.offsetWidth,
      panelHeight: panel.offsetHeight,
      viewport: {
        width: visual?.width ?? window.innerWidth,
        height: visual?.height ?? window.innerHeight,
        offsetTop: visual?.offsetTop ?? 0,
      },
    });

    panel.style.setProperty("--ds-popover-shift", `${placement.shiftX}px`);
    panel.style.setProperty("--ds-popover-max-h", `${placement.maxHeight}px`);
    setSide(placement.side);
  }, []);

  // Ref callback : mesure dès que le panneau existe dans le DOM.
  const panelRef = useCallback(
    (el: HTMLDivElement | null) => {
      panelElRef.current = el;
      if (el) measure();
    },
    [measure],
  );

  // La place DISPONIBLE ne dépend pas du contenu du panneau : changer de mode
  // (« Renommer », « Créer ») n'a pas à remesurer — la hauteur bornée et le
  // défilement interne suffisent à garder chaque option atteignable.
  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const visual = window.visualViewport;
    window.addEventListener("resize", measure);
    // `capture` : un défilement dans n'importe quel conteneur déplace l'ancre.
    window.addEventListener("scroll", measure, true);
    visual?.addEventListener("resize", measure);
    visual?.addEventListener("scroll", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      visual?.removeEventListener("resize", measure);
      visual?.removeEventListener("scroll", measure);
    };
  }, [open, measure]);

  return { panelRef, side };
}
